// src/pages/Careers.jsx
import { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import {
  Search, MapPin, Briefcase, Clock, ChevronRight,
  X, Send, Mail, Phone, CheckCircle2,
  ArrowRight, Filter, Loader2, Upload, FileText, Check
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const FontLink = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,600&family=Outfit:wght@300;400;500;600;700&display=swap');
  `}</style>
);

const API = 'https://api.smartlabtech.com';

const TYPE_COLOR = {
  'SERVICE': { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', dot: 'bg-sky-500' },
  'SALES SUPPORT': { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-500' },
  'SALES MANAGER': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  'SALES EXECUTIVE': { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-500' },
  'PRODUCTION': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  'ADMIN': { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', dot: 'bg-slate-500' },
  'TECHNICAL': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  'OTHER': { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-500' },
};
const FALLBACK_COLOR = TYPE_COLOR['OTHER'];

const formatPosted = (d) => {
  if (!d) return 'Recently';
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  return `${Math.floor(months / 12)} year${Math.floor(months / 12) > 1 ? 's' : ''} ago`;
};

const mapRoleToJob = (role) => ({
  id: role._id,
  title: role.roleName,
  dept: role.category || 'OTHER',
  type: role.category || 'OTHER',
  location: role.locations?.length ? role.locations.join(', ') : 'Remote',
  locations: role.locations || [],
  posted: formatPosted(role.postedOn || role.createdAt),
  desc: role.roleDescription || '',
  tags: role.tags || [],
  openings: role.noOfPositions || 1,
});

/* ─── Resume Upload Field ─── */
function ResumeUpload({ file, onChange, error, id }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const accept = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  const MAX_MB = 5;

  const handleFiles = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      onChange(null, `File must be under ${MAX_MB}MB`);
      return;
    }
    onChange(f, null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files?.[0]);
  };

  return (
    <div>
      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Resume (PDF or DOC) <span className="text-red-400">*</span>
      </label>

      {file ? (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200">
          <FileText size={16} className="text-emerald-600 flex-shrink-0" />
          <span className="text-sm text-emerald-800 truncate flex-1">{file.name}</span>
          <span className="text-[10px] text-emerald-600">{(file.size / 1024).toFixed(0)} KB</span>
          <button
            type="button"
            onClick={() => { onChange(null, null); if (inputRef.current) inputRef.current.value = ''; }}
            className="p-1 rounded-full text-emerald-600 hover:bg-emerald-100"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer flex flex-col items-center justify-center gap-1 px-3 py-4 rounded-xl border-2 border-dashed transition-colors ${
            error ? 'border-red-300 bg-red-50/40'
              : dragOver ? 'border-blue-500 bg-blue-50'
              : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/40'
          }`}
        >
          <Upload size={18} className={error ? 'text-red-400' : 'text-blue-500'} />
          <p className="text-xs text-slate-600 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <span className="font-semibold text-blue-600">Click to upload</span> or drag & drop
          </p>
          <p className="text-[10px] text-slate-400">PDF or DOC, max {MAX_MB}MB</p>
        </div>
      )}

      <input
        id={id}
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFiles(e.target.files?.[0])}
        className="hidden"
      />
      {error && <p className="text-[10px] text-red-400 mt-1">{error}</p>}
    </div>
  );
}

/* ─── Apply Modal (job-specific OR general with role dropdown) ─── */
function ApplyModal({ job, mode = 'job', roles = [], onClose }) {
  const isGeneral = mode === 'general';
  const [form, setForm] = useState({
    name: '', email: '', phone: '', city: '', message: '',
    roleId: isGeneral ? (roles[0]?.id || '') : '',
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const colors = job ? (TYPE_COLOR[job.type] || FALLBACK_COLOR) : TYPE_COLOR['OTHER'];

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
    setSubmitError('');
  };

  const handleResume = (file, err) => {
    setResumeFile(file);
    setErrors(prev => ({ ...prev, resume: err || undefined }));
    setSubmitError('');
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ''))) e.phone = 'Valid 10-digit number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (!resumeFile) e.resume = 'Resume is required';
    if (isGeneral && !form.roleId) e.roleId = 'Please select a role';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setSubmitError('');

    try {
      const fd = new FormData();
      fd.append('roleId', isGeneral ? form.roleId : job.id);
      fd.append('fullName', form.name.trim());
      fd.append('email', form.email.trim());
      fd.append('mobileNumber', form.phone.trim());
      fd.append('currentCity', form.city.trim());
      fd.append('coverNote', form.message.trim());
      fd.append('resume', resumeFile); // backend expects req.file

      const res = await fetch(`${API}/api/careers/apply`, {
        method: 'POST',
        body: fd, // ← no Content-Type; browser sets boundary
      });
      const data = await res.json();

      if (res.ok && data.success) setDone(true);
      else setSubmitError(data.message || 'Failed to submit. Please try again.');
    } catch (err) {
      setSubmitError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="relative flex-shrink-0 px-6 py-5 overflow-hidden bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)]">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
        <div className="flex items-start justify-between relative z-10 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
              <p className="text-[11px] font-bold text-sky-200 tracking-widest uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {isGeneral ? 'Submit Your Resume' : `${job.type.replace(/_/g, ' ')} · Apply Now`}
              </p>
            </div>
            <h3 className="text-xl font-bold text-white leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isGeneral ? 'Send us your Resume' : job.title}
            </h3>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {isGeneral ? (
                <span className="flex items-center gap-1 text-xs text-white/70" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <FileText size={11} /> We'll keep it on file for future openings
                </span>
              ) : (
                <>
                  <span className="flex items-center gap-1 text-xs text-white/70" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <MapPin size={11} /> {job.location}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-white/70" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <Briefcase size={11} /> {job.dept}
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-white/70 hover:bg-white/20 hover:text-white transition-all flex-shrink-0"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-white">
        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center p-10 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4"
              >
                <CheckCircle2 size={32} className="text-emerald-500" />
              </motion.div>
              <h4 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isGeneral ? 'Resume Submitted!' : 'Application Submitted!'}
              </h4>
              <p className="text-sm text-slate-500 leading-relaxed max-w-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Thank you, <strong className="text-blue-600">{form.name}</strong>. Our HR team will review your {isGeneral ? 'resume' : 'application'} and reach out within 3–5 business days.
              </p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] hover:shadow-lg transition-all"
              >
                Close
              </button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={handleSubmit}
              className="flex flex-col gap-3.5 p-6"
            >
              {/* Role dropdown for generic submission */}
              {isGeneral && (
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Applying For <span className="text-red-400">*</span>
                  </label>
                  {roles.length === 0 ? (
                    <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                      No open roles available right now. Please check back later.
                    </p>
                  ) : (
                    <>
                      <select
                        value={form.roleId}
                        onChange={handleChange('roleId')}
                        className={`w-full px-3 py-2.5 text-sm bg-slate-50 border rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all ${errors.roleId ? 'border-red-400' : 'border-slate-200'}`}
                      >
                        <option value="" disabled>Select a role…</option>
                        {roles.map(r => (
                          <option key={r.id} value={r.id}>{r.title} · {r.dept}</option>
                        ))}
                      </select>
                      {errors.roleId && <p className="text-[10px] text-red-400 mt-0.5">{errors.roleId}</p>}
                    </>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Full Name *</label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={handleChange('name')}
                    className={`w-full px-3 py-2.5 text-sm bg-slate-50 border rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all ${errors.name ? 'border-red-400' : 'border-slate-200'}`}
                  />
                  {errors.name && <p className="text-[10px] text-red-400 mt-0.5">{errors.name}</p>}
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Phone *</label>
                  <input
                    type="tel"
                    placeholder="10-digit number"
                    value={form.phone}
                    onChange={handleChange('phone')}
                    className={`w-full px-3 py-2.5 text-sm bg-slate-50 border rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all ${errors.phone ? 'border-red-400' : 'border-slate-200'}`}
                  />
                  {errors.phone && <p className="text-[10px] text-red-400 mt-0.5">{errors.phone}</p>}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Email Address *</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={handleChange('email')}
                  className={`w-full px-3 py-2.5 text-sm bg-slate-50 border rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all ${errors.email ? 'border-red-400' : 'border-slate-200'}`}
                />
                {errors.email && <p className="text-[10px] text-red-400 mt-0.5">{errors.email}</p>}
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Current City</label>
                <input
                  type="text"
                  placeholder="City you're based in"
                  value={form.city}
                  onChange={handleChange('city')}
                  className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                />
              </div>

              {/* Resume upload */}
              <ResumeUpload
                id={`resume-${isGeneral ? 'general' : 'job'}`}
                file={resumeFile}
                onChange={handleResume}
                error={errors.resume}
              />

              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {isGeneral ? 'About You (Optional)' : 'Cover Note (Optional)'}
                </label>
                <textarea
                  rows={3}
                  placeholder={isGeneral
                    ? 'Tell us about your experience, skills, and the kind of role you’re looking for...'
                    : "Tell us about your experience and why you're a great fit..."}
                  value={form.message}
                  onChange={handleChange('message')}
                  className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                />
              </div>

              {submitError && (
                <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{submitError}</p>
              )}

              <button
                type="submit"
                disabled={loading || (isGeneral && roles.length === 0)}
                className="relative overflow-hidden flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-bold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-white/20 before:to-transparent before:transition-transform before:duration-500 hover:before:translate-x-0"
              >
                {loading ? (
                  <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                ) : (
                  <>
                    <Send size={14} /> {isGeneral ? 'Submit Resume' : 'Submit Application'}
                  </>
                )}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ─── Reveal wrapper ─── */
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Job Card ─── */
function JobCard({ job, index, onApply }) {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const colors = TYPE_COLOR[job.type] || FALLBACK_COLOR;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: index * 0.07, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="bg-white border border-slate-100 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-blue-200 hover:-translate-y-1 group"
    >
      <div className="h-1 w-full bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)]" />

      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full border mb-2.5 ${colors.bg} ${colors.text} ${colors.border}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Briefcase size={9} />
              {job.dept}
            </span>
            <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug group-hover:text-blue-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
              {job.title}
            </h3>
          </div>
          <div className="flex-shrink-0 text-center bg-[linear-gradient(135deg,rgba(15,35,86,0.08)_0%,rgba(30,58,138,0.06)_50%,rgba(14,165,233,0.08)_100%)] border border-blue-100 rounded-xl px-3 py-2">
            <p className="text-xl font-bold text-blue-700 leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>{job.openings}</p>
            <p className="text-[9px] text-blue-400 font-semibold uppercase tracking-wider mt-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Opening{job.openings > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-4">
          <span className="flex items-center gap-1.5 text-xs text-slate-500" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <MapPin size={12} className="text-blue-400" /> {job.location}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <Clock size={12} className="text-slate-300" /> Posted {job.posted}
          </span>
        </div>

        {job.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {job.tags.map((tag, i) => (
              <span key={i} className="text-[10px] font-semibold px-2 py-0.5 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden mb-4"
            >
              {job.desc && <p className="text-sm text-slate-500 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>{job.desc}</p>}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-semibold text-blue-600 flex items-center gap-1 hover:text-blue-800 transition-colors"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {expanded ? 'Show less' : 'View details'}
            <motion.span animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronRight size={13} />
            </motion.span>
          </button>
          <button
            onClick={() => onApply(job)}
            className="relative overflow-hidden flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-white/20 before:to-transparent before:transition-transform before:duration-500 hover:before:translate-x-0"
          >
            Apply Now <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Main Page ─── */
export default function Careers() {
  const [heroData, setHeroData] = useState({
    title: 'Build Your Career With Purpose.',
    tag: 'SmartLabTech · Careers',
    description: 'Join SmartLabTech — helping world-class scientific instruments reach research labs, pharma companies, and universities across India.',
    image: 'https://images.unsplash.com/photo-1582719471384-894fbb16e074?auto=format&fit=crop&w=1920&q=80',
  });
  const [ctaData, setCtaData] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [keyword, setKeyword] = useState('');
  const [typeFilter, setType] = useState('All');
  const [locFilter, setLoc] = useState('All');
  const [applyJob, setApplyJob] = useState(null);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);

  const [jobTypes, setJobTypes] = useState(['All']);
  const [locations, setLocations] = useState(['All']);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/careers/page`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data.success && data.data) {
            const hero = data.data.hero || {};
            if (hero.title) {
              setHeroData({
                title: hero.title,
                tag: hero.tag || 'SmartLabTech · Careers',
                description: hero.description || '',
                image: hero.image || heroData.image,
              });
            }
            if (data.data.cta) setCtaData(data.data.cta);
          }
        }
      } catch (err) {
        console.error('Page fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingJobs(true);
        setFetchError('');
        const res = await fetch(`${API}/api/careers/roles`);
        const data = await res.json();
        if (!cancelled && data.success) {
          const mapped = (data.data || []).map(mapRoleToJob);
          setJobs(mapped);
          const types = ['All', ...new Set(mapped.map(j => j.type).filter(Boolean))];
          const locs = new Set();
          mapped.forEach(j => (j.locations || []).forEach(l => locs.add(l)));
          setJobTypes(types);
          setLocations(['All', ...Array.from(locs)]);
        } else if (!cancelled) {
          setFetchError(data.message || 'Failed to load roles');
        }
      } catch (err) {
        if (!cancelled) setFetchError('Failed to connect to server');
      } finally {
        if (!cancelled) setLoadingJobs(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = jobs.filter(j => {
    const kw = keyword.toLowerCase();
    const matchKw = !kw || j.title.toLowerCase().includes(kw) || j.dept.toLowerCase().includes(kw) || j.location.toLowerCase().includes(kw) || (j.tags || []).some(t => t.toLowerCase().includes(kw));
    const matchType = typeFilter === 'All' || j.type === typeFilter;
    const matchLoc = locFilter === 'All' || (j.locations || []).includes(locFilter) || j.location.includes(locFilter);
    return matchKw && matchType && matchLoc;
  });

  const clearFilters = () => { setKeyword(''); setType('All'); setLoc('All'); };
  const hasFilters = keyword || typeFilter !== 'All' || locFilter !== 'All';

  return (
    <>
      <FontLink />
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden" style={{ minHeight: 'clamp(580px, 85vh, 820px)' }}>
        <motion.div className="absolute inset-0" style={{ y: imgY }}>
          <img src={heroData.image} alt="Laboratory instruments" className="w-full h-full object-cover object-center scale-110" />
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(15,35,86,0.88)_0%,rgba(30,58,138,0.72)_50%,rgba(14,165,233,0.82)_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_top,#0f2356_0%,transparent_55%)]" />
        </motion.div>

        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '64px 64px'
        }} />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none bg-gradient-to-bl from-sky-500/10 to-transparent rounded-full blur-3xl" />

        <div className="relative z-10 max-w-8xl mx-auto px-4 sm:px-8 lg:px-20 flex flex-col justify-center h-full"
          style={{ paddingTop: 'clamp(80px,12vw,140px)', paddingBottom: 'clamp(60px,10vw,120px)' }}>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="w-8 h-px bg-sky-400" />
            <span className="text-[11px] font-semibold tracking-[0.3em] uppercase text-sky-300" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {heroData.tag}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-bold text-white leading-[0.92] mb-6"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
          >
            {heroData.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.22 }}
            className="text-base sm:text-lg leading-relaxed mb-10 max-w-lg text-white/80"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {heroData.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.34 }}
            className="flex flex-wrap gap-3"
          >
            <a href="#listings" className="relative overflow-hidden inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-bold bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] text-white hover:shadow-lg hover:-translate-y-0.5 transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-white/20 before:to-transparent before:transition-transform before:duration-500 hover:before:translate-x-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
              View Open Roles <ArrowRight size={15} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Listings */}
      <section id="listings" className="bg-slate-50 py-16">
        <div className="max-w-8xl mx-auto px-4 sm:px-8 lg:px-20">
          <Reveal className="mb-10">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-px bg-blue-600" />
                  <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-blue-600" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Job Archives
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Open <span className="bg-gradient-to-r from-blue-700 to-sky-500 bg-clip-text text-transparent">Positions</span>
                </h2>
              </div>
              <p className="text-sm text-slate-500" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {loadingJobs ? 'Loading…' : `${filtered.length} position${filtered.length !== 1 ? 's' : ''} found`}
              </p>
            </div>
          </Reveal>

          <div className="flex flex-col lg:flex-row gap-8 items-start">
            <Reveal delay={0.1} className="w-full lg:w-64 flex-shrink-0 lg:sticky lg:top-28">
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <Filter size={13} className="text-blue-700" />
                    <span className="text-sm font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Filters</span>
                  </div>
                  {hasFilters && (
                    <button onClick={clearFilters} className="flex items-center gap-1 text-[11px] font-semibold text-red-400 hover:text-red-600 transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      <X size={10} /> Clear all
                    </button>
                  )}
                </div>

                <div className="mb-5">
                  <p className="text-[10px] font-semibold uppercase tracking-widest mb-2 text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>Keywords</p>
                  <div className="relative">
                    <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="text" placeholder="Title, skill…" value={keyword} onChange={e => setKeyword(e.target.value)} className="w-full pl-8 pr-3 py-2.5 text-sm border-2 border-slate-200 rounded-xl bg-slate-50 outline-none focus:border-blue-500 focus:bg-white transition-all" />
                  </div>
                </div>

                <div className="mb-5">
                  <p className="text-[10px] font-semibold uppercase tracking-widest mb-2.5 text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>Category</p>
                  <div className="flex flex-col gap-1">
                    {jobTypes.map(t => (
                      <button key={t} onClick={() => setType(t)} className={`w-full text-left px-3 py-2 rounded-xl text-xs font-semibold transition-all ${typeFilter === t ? 'text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)]' : 'text-slate-500 hover:bg-slate-50 hover:text-blue-700'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {t === 'All' ? 'All Categories' : t}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest mb-2.5 text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>Location</p>
                  <div className="flex flex-col gap-1">
                    {locations.map(l => (
                      <button key={l} onClick={() => setLoc(l)} className={`w-full text-left px-3 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 ${locFilter === l ? 'text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)]' : 'text-slate-500 hover:bg-slate-50 hover:text-blue-700'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {l !== 'All' && <MapPin size={10} />}
                        {l === 'All' ? 'All Locations' : l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="flex-1 min-w-0">
              {hasFilters && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {keyword && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full">
                      "{keyword}"
                      <button onClick={() => setKeyword('')} className="text-blue-400 hover:text-blue-700"><X size={11} /></button>
                    </span>
                  )}
                  {typeFilter !== 'All' && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-3 py-1 rounded-full">
                      {typeFilter}
                      <button onClick={() => setType('All')} className="text-sky-400 hover:text-sky-700"><X size={11} /></button>
                    </span>
                  )}
                  {locFilter !== 'All' && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full">
                      <MapPin size={10} /> {locFilter}
                      <button onClick={() => setLoc('All')} className="text-slate-400 hover:text-slate-700"><X size={11} /></button>
                    </span>
                  )}
                </div>
              )}

              {loadingJobs ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-100">
                  <Loader2 size={36} className="animate-spin text-blue-600 mb-3" />
                  <p className="text-sm text-slate-500" style={{ fontFamily: "'Outfit', sans-serif" }}>Loading open positions…</p>
                </div>
              ) : fetchError ? (
                <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-slate-100">
                  <p className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Couldn't load jobs</p>
                  <p className="text-sm text-slate-400 mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>{fetchError}</p>
                  <button onClick={() => window.location.reload()} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] hover:shadow-lg transition-all">Retry</button>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-slate-100">
                  <p className="text-4xl mb-4">🔍</p>
                  <p className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>No positions found</p>
                  <p className="text-sm text-slate-400 mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>Try adjusting your filters or keywords</p>
                  <button onClick={clearFilters} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] hover:shadow-lg transition-all">Clear Filters</button>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {filtered.map((job, i) => (
                    <JobCard key={job.id} job={job} index={i} onApply={setApplyJob} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <Reveal>
        <div className="py-16 px-4 bg-slate-50">
          <div className="max-w-8xl mx-auto px-4 sm:px-8 lg:px-20">
            <div className="rounded-2xl p-8 sm:p-12 flex flex-wrap items-center justify-between gap-6 bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] shadow-xl">
              <div>
                <p className="text-[11px] font-bold text-sky-200 tracking-widest uppercase mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {ctaData?.tag || "Can't find your role?"}
                </p>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {ctaData?.title || 'Send us your Resume'}
                </h3>
                <p className="text-sm text-white/70 max-w-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {ctaData?.description || "We're always looking for talented people. Drop your CV and we'll keep it on file for future openings."}
                </p>
              </div>
              <div>
                <button
                  onClick={() => setResumeModalOpen(true)}
                  className="relative overflow-hidden inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-sm font-bold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] border-2 border-white/20 hover:shadow-lg hover:-translate-y-0.5 transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-white/20 before:to-transparent before:transition-transform before:duration-500 hover:before:translate-x-0"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  <Upload size={15} /> Submit Resume
                </button>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <Footer />

      {/* Apply Modal (job-specific) */}
      <AnimatePresence>
        {applyJob && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setApplyJob(null)} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[2000]" />
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              onClick={e => e.stopPropagation()}
              className="fixed inset-0 flex items-center justify-center p-4 sm:p-6 z-[2001]"
            >
              <div className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl">
                <ApplyModal job={applyJob} mode="job" onClose={() => setApplyJob(null)} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Resume Modal (generic submission from CTA — needs role dropdown) */}
      <AnimatePresence>
        {resumeModalOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setResumeModalOpen(false)} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[2000]" />
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              onClick={e => e.stopPropagation()}
              className="fixed inset-0 flex items-center justify-center p-4 sm:p-6 z-[2001]"
            >
              <div className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl">
                <ApplyModal
                  job={null}
                  mode="general"
                  roles={jobs}
                  onClose={() => setResumeModalOpen(false)}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}